import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';

import { DateTimeFormat, DateTimeHelper, DayjsFormatMapper } from '@xemplo/date-time';

import {
  allowedDateFieldEntry,
  applyDateMask,
  getDisplayValue,
} from '../date-field.helper';
import { DateFieldProps } from '../date-field.types';

dayjs.extend(utc);

/**
 * Converts raw input text into the date-field value pipeline, with date masking.
 *
 * Applies a XX/XX/XXXX mask so "/" separators are auto-inserted as the user types.
 * Emits a synthetic event with the masked value so display and parsing stay in sync.
 *
 * Emits:
 * - a `Date` when parsing succeeds,
 * - `null` when the input is non-empty but not yet a valid date,
 * - `undefined` when the user clears the input.
 */
export function useDateFieldEvent({
  onChange,
  onKeyDown,
  onValueChange,
  value,
  dateFormat = DateTimeFormat.Default,
}: DateFieldProps) {
  /**
   * Tracks the last-rendered display value so `applyDateMask` always has an accurate base
   * for its deletion-vs-typing heuristic (`isDeleting = value.length < prevValue.length`).
   *
   * `handleChange` also updates `prevValueRef.current` synchronously after every mask application,
   * so the ref stays current across consecutive keystrokes. This `useEffect` covers the case where
   * `value` or `dateFormat` changes externally (e.g. a programmatic `form.setValue`) — it keeps
   * the ref in sync after such re-renders so the next keystroke uses the correct base.
   */
  const prevValueRef = useRef(getDisplayValue(value, dateFormat));

  useEffect(() => {
    prevValueRef.current = getDisplayValue(value, dateFormat);
  }, [value, dateFormat]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (!allowedDateFieldEntry(event)) event.preventDefault();
    },
    [onKeyDown],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const maskedValue = applyDateMask(rawValue, prevValueRef.current, dateFormat);
      prevValueRef.current = maskedValue;

      const effectiveEvent =
        maskedValue !== rawValue
          ? {
              ...event,
              target: { ...event.target, value: maskedValue },
              currentTarget: { ...event.currentTarget, value: maskedValue },
            }
          : event;

      onChange?.(effectiveEvent);
      if (!onValueChange) return;

      if (!maskedValue) {
        onValueChange(undefined);
        return;
      }

      const isValid = DateTimeHelper.isValidDate(maskedValue, dateFormat);
      if (!isValid) {
        onValueChange(null);
        return;
      }

      const date = dayjs(maskedValue, [dateFormat, DayjsFormatMapper[dateFormat]])
        .utc()
        .toDate();
      onValueChange(date);
    },
    [onChange, onValueChange, dateFormat],
  );

  return {
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  };
}
